import { t } from "@lingui/macro";
import { useDeleteContractorsBulkMutation } from "@src/__generated__/graphql";
import { ContractorOrderByColumnEnum } from "@src/__generated__/urql-graphql";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  Link,
  ModalConfirm,
} from "@src/components/ui-kit";
import type { BulkAction } from "@src/components/widgets/bulk-actions/types";
import { can } from "@src/utils/components/permissions";
import { formatIBAN } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import React, { useContext, useMemo, useState } from "react";
import type { ContractorModel } from "../models";
import { PageContractorsCtx } from "../PageContractors";
import { PopoverRowActions } from "./PopoverRowActions";

type TData = {
  id: string;
  legalName: ContractorModel["legalName"];
  registrationNo: ContractorModel["registrationNo"];
  isVatPayer: ContractorModel["isVatPayer"];
  iban: ContractorModel["iban"];
  actions: string;
};

const getColumns = (): BodyColumnDef<TData, ContractorOrderByColumnEnum>[] => [
  {
    id: "legalName",
    meta: { label: t`Title` },
    header: () => t`Title`,
    orderBy: ContractorOrderByColumnEnum.LegalName,
    cell: ({ row }) => {
      const r = row.original;

      return (
        <div className="flex items-center">
          <Link href={{ pathname: "/contractors/detail", query: { id: r.id } }}>
            {r.legalName}
          </Link>
        </div>
      );
    },
  },
  {
    id: "registrationNo",
    meta: { label: t`Reg. no` },
    header: () => t`Reg. no`,
    orderBy: ContractorOrderByColumnEnum.RegistrationNo,
    cell: ({ row }) => row.original.registrationNo,
  },
  {
    id: "iban",
    meta: { label: t`IBAN` },
    header: () => t`IBAN`,
    orderBy: ContractorOrderByColumnEnum.Iban,
    cell: ({ row }) => {
      const { iban } = row.original;

      return iban && formatIBAN(iban);
    },
  },
  {
    id: "isVatPayer",
    meta: { label: t`VAT payer` },
    header: () => t`VAT payer`,
    orderBy: ContractorOrderByColumnEnum.IsVatPayer,
    cell: ({ row }) => row.original.isVatPayer,
  },
  {
    id: ACTIONS_COLUMN_ID,
    meta: { label: t`Actions` },
    header: () => t`Actions`,
    cell: ({ row }) => {
      const r = row.original;

      return can("contractor_delete", "contractor_update") ? (
        <div className="flex items-left">
          <PopoverRowActions id={r.id} />
        </div>
      ) : null;
    },
  },
];

export const TableContractors = observer(function TableContractors() {
  const store = useContext(PageContractorsCtx);
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const data: TData[] = store.contractors.map((i) => ({
    id: i.id,
    legalName: i.legalName,
    registrationNo: i.registrationNo,
    isVatPayer: i.isVatPayer,
    iban: i.iban,
    actions: "",
  }));

  const [deleteContractorsBulk, deleteContractorsState] =
    useDeleteContractorsBulkMutation({
      onCompleted() {
        setIsDeleteModalOpen(false);
        store.selectedContractorIds = [];
        store.refetch?.();
      },
    });

  const bulkActions = useMemo<BulkAction[]>(
    () => [
      {
        id: "contractors-actions",
        label: () => t`Actions`,
        icon: "dots-vertical",
        isLoading: () => deleteContractorsState.loading,
        subActions: can("contractor_delete")
          ? [
              {
                id: "delete",
                label: () => t`Delete`,
                isDestructive: true,
                callback: (selectedIds: string[] | Set<string>) => {
                  setIdsToDelete(Array.from(selectedIds));
                  setIsDeleteModalOpen(true);
                },
              },
            ]
          : [],
      },
    ],
    [deleteContractorsState.loading],
  );

  return (
    <React.Fragment>
      <DataTable.Body
        tableKey="contractors"
        data={data}
        columns={getColumns()}
        orderedBy={store.orderBy.clauses[0]}
        onOrderByChange={(clause) => {
          if (!clause.order) {
            store.orderBy.setClauses([]);
            return;
          }

          store.orderBy.setClauses([
            { column: clause.column, order: clause.order },
          ]);
        }}
        selected={store.selectedContractorIds}
        onSelect={(ids) => (store.selectedContractorIds = ids)}
        allIds={store.allContractorIds}
        enableRowSelection
        bulkActions={bulkActions}
      />
      <ModalConfirm
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() =>
          deleteContractorsBulk({ variables: { id: idsToDelete } })
        }
        isLoading={deleteContractorsState.loading}
      />
    </React.Fragment>
  );
});
